// frontend/src/components/LanguageGate.tsx
import { useEffect, useMemo, useState } from "react";
import i18n from "@/i18n";
import { useAuth } from "@/context/use-auth";
import { LOCAL_STORAGE_LANG_KEY } from "@/i18n";

/**
 * LanguageGate prevents flicker by delaying the first paint until
 * the correct language is known.
 *
 * Behavior:
 * - While Auth is loading, render nothing.
 * - Once Auth resolves:
 *    - If user.language exists => use it (persist to localStorage).
 *    - Else (not logged-in or no pref) => use detector/localStorage result that i18n already picked.
 * - Also sets <html lang="..."> for a11y/SEO.
 */
export default function LanguageGate({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const [ready, setReady] = useState(false);

  const targetLang = useMemo(() => {
    // If the user is known and has a saved preference, we prefer that.
    if (user?.language) return user.language;

    // Otherwise i18next’s detector already picked something (localStorage or navigator).
    return i18n.resolvedLanguage || i18n.language || "en";
  }, [user?.language]);

  useEffect(() => {
    if (isLoading) return;

    // Apply the resolved language only after auth is settled to avoid flips
    const apply = async () => {
      if (i18n.language !== targetLang) {
        await i18n.changeLanguage(targetLang);
      }
      // Keep HTML in sync
      document?.documentElement?.setAttribute("lang", targetLang);
      // For future boots before /me resolves
      try {
        localStorage.setItem(LOCAL_STORAGE_LANG_KEY, targetLang);
      } catch {
        // ignore (e.g., in private mode)
      }
      setReady(true);
    };

    apply();
  }, [isLoading, targetLang]);

  if (!ready) {
    // Render nothing (no text) => guarantees no language flicker.
    // You could show a branded splash here if you prefer.
    return null;
  }

  return <>{children}</>;
}